// HomeActivityFeed.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Hooks
import usePersonalGlobalActivityFeed from 'home/activity/hooks/usePersonalGlobalActivityFeed';

// Utilities
import { groupActivityBursts } from 'home/activity/utils/groupActivityBursts';
import {
  readHomeActivityShowDetails,
  writeHomeActivityShowDetails,
} from 'home/activity/utils/homeActivityDetailsPref';

// CSS
import './HomeActivityFeed.css';

// Components
import HomeActivityBurst from 'home/activity/HomeActivityBurst';

/**
 * HomeActivityFeed
 *
 * Prototype: time-ordered personal activity across nodes the user created
 * or is assigned to.
 *
 * @param {object} props
 * @param {string} props.baseKey
 * @param {string} props.userKey
 * @param {function} [props.onOpenNode] - Opens a node via type policy (Peek or Workspace).
 * @returns {ReactNode}
 */
const HomeActivityFeed = React.memo(({
  baseKey,
  userKey,
  onOpenNode,
}) => {
  const [showDetails, setShowDetails] = useState(() => readHomeActivityShowDetails());

  const {
    items,
    loading,
    error,
  } = usePersonalGlobalActivityFeed({
    baseKey,
    includeDetails: showDetails,
    userKey,
  });

  const bursts = useMemo(() => groupActivityBursts(items), [items]);

  const handleToggleDetails = useCallback(() => {
    setShowDetails((current) => {
      const next = !current;
      writeHomeActivityShowDetails(next);
      return next;
    });
  }, []);

  return (
    <div className="home-activity-feed-container">

      {/* HEADER */}
      <div className="home-activity-feed-header">

        {/* TITLE ROW */}
        <div className="home-activity-feed-title-row">
          <div className="home-activity-feed-title">Activity</div>
          <button
            className="home-activity-feed-details-toggle"
            type="button"
            onClick={handleToggleDetails}
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {/* SUBTITLE 
        <div className="home-activity-feed-subtitle">
          Updates on favorites and items you created or are assigned to
        </div>
        */}
      </div>

      {/* LIST */}
      <div className="home-activity-feed-list">
        {loading ? (
          <div className="home-activity-feed-empty">Loading activity…</div>
        ) : null}

        {!loading && error && items.length === 0 ? (
          <div className="home-activity-feed-empty">{error}</div>
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <div className="home-activity-feed-empty">
            No recent activity on favorites or items you created or are assigned to.
          </div>
        ) : null}

        {bursts.map((burst) => (
          <HomeActivityBurst
            key={burst.key}
            burst={burst}
            onOpenNode={onOpenNode}
          />
        ))}
      </div>
    </div>
  );
});

HomeActivityFeed.displayName = 'HomeActivityFeed';

export default HomeActivityFeed;
